/* import __COLOCATED_TEMPLATE__ from './line-card.hbs'; */
/* RESPONSIBLE TEAM: team-fin-voice */
import Component from '@glimmer/component';
import type FinVoiceNumberConfiguration from 'embercom/models/fin-voice/number-configuration';
import type AppService from 'embercom/services/app-service';
import type FinVoiceConfigService from 'embercom/services/fin-voice-config-service';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import {
  DEFAULT_VOICE,
  FIN_VOICE_11LABS_VOICES,
  type FinVoiceVoice,
} from 'embercom/objects/ai-agent/fin-voice/fin-voice-voice-options';
import {
  DEFAULT_VOICE_LANGUAGE,
  FIN_VOICE_VOICE_LANGUAGE_OPTIONS,
  type FinVoiceVoiceLanguage,
} from 'embercom/objects/ai-agent/fin-voice/fin-voice-language-options';

interface Args {
  numberConfiguration: FinVoiceNumberConfiguration;
  phoneNumberId: string;
  setSelectedLine: (
    phoneNumberId: string,
    numberConfiguration: FinVoiceNumberConfiguration,
  ) => void;
}

interface Signature {
  Element: HTMLElement;
  Args: Args;
}

export default class LineCard extends Component<Signature> {
  @service declare finVoiceConfigService: FinVoiceConfigService;
  @service declare appService: AppService;
  @service declare intl: IntlService;

  get defaultVoice(): FinVoiceVoice {
    if (this.appService.app.canUseFinVoiceDefaultVoiceLydia) {
      // eslint-disable-next-line @intercom/intercom/no-bare-strings
      return 'Lydia';
    }
    return DEFAULT_VOICE;
  }

  @tracked officeHoursScheduleComparison: 'in' | 'nin' | 'anytime' = 'anytime';
  @tracked percentageRolloutInHours: number | undefined;
  @tracked percentageRolloutNinHours: number | undefined;
  @tracked selectedOfficeHoursSchedule?: string;
  @tracked selectedLanguage: FinVoiceVoiceLanguage = DEFAULT_VOICE_LANGUAGE;
  @tracked voice?: FinVoiceVoice;

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);
    this.populateInitialBehavior();
    this.populateInitialOfficeHoursSchedules();
  }

  get phoneNumbers() {
    return this.finVoiceConfigService.phoneNumbers;
  }

  get phoneNumber() {
    return this.phoneNumbers[this.phoneNumberId];
  }

  get phoneNumberId() {
    return this.args.phoneNumberId;
  }

  get numberConfiguration() {
    return this.args.numberConfiguration;
  }

  get savedOfficeHoursScheduleComparison() {
    return this.numberConfiguration?.predicates?.officeHoursComparison || 'anytime';
  }

  get savedOfficeHoursSchedule() {
    return this.numberConfiguration?.predicates?.officeHoursSchedule;
  }

  get officeHoursSchedules() {
    return this.finVoiceConfigService.officeHoursSchedules;
  }

  get activeOfficeHoursScheduleDescription() {
    return this.officeHoursSchedules.find(
      (schedule) => schedule.value === this.selectedOfficeHoursSchedule,
    )?.description;
  }

  get hasSavedPercentageRollout() {
    return Object.keys(this.numberConfiguration?.predicates || {}).some((key) =>
      ['in', 'nin', 'default'].includes(key),
    );
  }

  get isExternalProvider() {
    return this.phoneNumber?.isExternalProvider;
  }

  get officeHoursTz() {
    return this.activeOfficeHoursScheduleDescription;
  }

  get inRolloutValue() {
    return `${this.percentageRolloutInHours}%`;
  }

  get outRolloutValue() {
    return `${this.percentageRolloutNinHours}%`;
  }

  get activeVoiceLabel() {
    let savedLanguage = this.selectedLanguage || DEFAULT_VOICE_LANGUAGE;

    let voiceText =
      FIN_VOICE_11LABS_VOICES.find((voice) => voice.value === this.savedVoice)?.text || '';

    let languageData = FIN_VOICE_VOICE_LANGUAGE_OPTIONS.find(
      (language) => language.value === savedLanguage,
    );
    let savedLanguageText = languageData?.text ? this.intl.t(languageData.text) : '';

    return [savedLanguageText, voiceText].join(' • ');
  }

  get savedVoice() {
    return this.numberConfiguration?.voice || this.defaultVoice;
  }

  @action
  populateInitialOfficeHoursSchedules() {
    this.selectedOfficeHoursSchedule =
      this.savedOfficeHoursSchedule || this.finVoiceConfigService.getDefaultOfficeHoursSchedule();

    let { inHours, ninHours } = this.calculateRolloutPercentages();

    this.percentageRolloutInHours = inHours;
    this.percentageRolloutNinHours = ninHours;

    this.officeHoursScheduleComparison = this.savedOfficeHoursScheduleComparison;
  }

  @action
  populateInitialBehavior() {
    // Get language from phone number, fallback to default
    let phoneNumberId = this.args.phoneNumberId;
    let phoneNumberData = phoneNumberId
      ? this.finVoiceConfigService.phoneNumbers[phoneNumberId]
      : undefined;

    this.selectedLanguage = (phoneNumberData?.locale ||
      DEFAULT_VOICE_LANGUAGE) as FinVoiceVoiceLanguage;

    this.voice = this.savedVoice || this.defaultVoice;
  }

  private calculateRolloutPercentages(): { inHours: number; ninHours: number } {
    let predicates = this.numberConfiguration?.predicates;
    let rolloutPercentageIn = 0;
    let rolloutPercentageNin = 0;

    if (this.hasSavedPercentageRollout) {
      rolloutPercentageIn =
        predicates?.in?.percentageRollout || predicates?.default?.percentageRollout || 0;

      rolloutPercentageNin =
        predicates?.nin?.percentageRollout || predicates?.default?.percentageRollout || 0;
    } else if (predicates?.officeHoursComparison === 'in') {
      rolloutPercentageIn = predicates?.percentageRollout || 0;
      rolloutPercentageNin = 0;
    } else if (predicates?.officeHoursComparison === 'nin') {
      rolloutPercentageIn = 0;
      rolloutPercentageNin = predicates?.percentageRollout || 0;
    } else {
      let defaultRollout = predicates?.percentageRollout || 0;
      rolloutPercentageIn = defaultRollout;
      rolloutPercentageNin = defaultRollout;
    }

    return { inHours: rolloutPercentageIn, ninHours: rolloutPercentageNin };
  }

  @action
  getHeaderLabel(phoneNumberId: string) {
    let phoneNumberRecord = this.phoneNumbers[phoneNumberId];

    if (!phoneNumberRecord) {
      return phoneNumberId;
    }

    if (phoneNumberRecord.name) {
      return this.intl.t(
        'operator.fin.setup.tabs.setup-and-go-live.phone.workspace-phone-number.phone-number-label',
        {
          label: phoneNumberRecord.name,
          phoneNumber: phoneNumberRecord.phoneNumber,
        },
      );
    }

    return phoneNumberRecord.phoneNumber;
  }

  @action
  editLine() {
    this.args.setSelectedLine(this.phoneNumberId, this.numberConfiguration);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::Deploy::Voice::LineCard': typeof LineCard;
  }
}
